import React, { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';

interface CompanyData {
  companyName: string;
  email: string;
  phone: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

const Safety: React.FC = () => {
  const [companyData, setCompanyData] = useState<CompanyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadCompanyInfo = async () => {
      try {
        const data = await apiRequest<CompanyData>(API_ENDPOINTS.COMPANY.PUBLIC);
        setCompanyData(data);
      } catch (error) {
        console.error('Error loading company info:', error);
      } finally {
        setLoading(false);
      }
    };
    loadCompanyInfo();
  }, []);
  return (
    <div className="bg-neutral-50 min-h-screen">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Fireworks Safety Guidelines
            </h1>
            <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
              Your safety is our top priority. Follow these guidelines for safe and enjoyable fireworks use.
            </p>
          </div>

          {/* Content */}
          <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12 space-y-8">
            {/* Important Safety Notice */}
            <section className="bg-red-50 border-2 border-red-200 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-red-800 mb-4 flex items-center">
                <span className="mr-3">🚨</span>
                CRITICAL SAFETY NOTICE
              </h2>
              <div className="text-red-700 space-y-3">
                <p className="font-semibold text-lg">Fireworks can cause serious injury or death if not handled properly.</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>NEVER allow children to handle fireworks without adult supervision</li>
                  <li>ALWAYS read and follow all instructions before use</li>
                  <li>NEVER attempt to relight malfunctioned fireworks</li>
                  <li>Keep water or fire extinguisher nearby at all times</li>
                </ul>
              </div>
            </section>

            {/* Before You Start */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">📋</span>
                Before You Start
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-2 flex items-center">
                      <span className="mr-2">📖</span>
                      Read All Instructions
                    </h3>
                    <ul className="text-blue-700 text-sm space-y-1">
                      <li>• Examine each firework carefully</li>
                      <li>• Understand the lighting procedure</li>
                      <li>• Know the expected effects and duration</li>
                      <li>• Check for any damage before use</li>
                    </ul>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h3 className="font-semibold text-green-800 mb-2 flex items-center">
                      <span className="mr-2">👥</span>
                      Designate Responsible Adults
                    </h3>
                    <ul className="text-green-700 text-sm space-y-1">
                      <li>• Only sober adults should handle fireworks</li>
                      <li>• Assign one person to light fireworks</li>
                      <li>• Have someone monitor the safety area</li>
                      <li>• Keep children at safe distance</li>
                    </ul>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-purple-50 rounded-lg p-4">
                    <h3 className="font-semibold text-purple-800 mb-2 flex items-center">
                      <span className="mr-2">🏞️</span>
                      Choose Safe Location
                    </h3>
                    <ul className="text-purple-700 text-sm space-y-1">
                      <li>• Large, open outdoor area</li>
                      <li>• Away from buildings and vehicles</li>
                      <li>• No dry grass or flammable materials</li>
                      <li>• Check local laws and restrictions</li>
                    </ul>
                  </div>
                  <div className="bg-orange-50 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-800 mb-2 flex items-center">
                      <span className="mr-2">🧯</span>
                      Prepare Safety Equipment
                    </h3>
                    <ul className="text-orange-700 text-sm space-y-1">
                      <li>• Water hose or large bucket of water</li>
                      <li>• Fire extinguisher (recommended)</li>
                      <li>• First aid kit nearby</li>
                      <li>• Mobile phone for emergencies</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Safe Distance Guidelines */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">📏</span>
                Safe Distance Guidelines
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse bg-neutral-50 rounded-lg">
                  <thead>
                    <tr className="bg-neutral-200">
                      <th className="text-left p-4 font-semibold text-neutral-900">Firework Type</th>
                      <th className="text-left p-4 font-semibold text-neutral-900">Minimum Distance</th>
                      <th className="text-left p-4 font-semibold text-neutral-900">Spectator Distance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-neutral-200">
                      <td className="p-4 text-neutral-700">Sparklers</td>
                      <td className="p-4 text-neutral-700">5 feet from others</td>
                      <td className="p-4 text-neutral-700">10 feet</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="p-4 text-neutral-700">Ground Spinners</td>
                      <td className="p-4 text-neutral-700">10 feet from buildings</td>
                      <td className="p-4 text-neutral-700">25 feet</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="p-4 text-neutral-700">Fountains</td>
                      <td className="p-4 text-neutral-700">15 feet from buildings</td>
                      <td className="p-4 text-neutral-700">35 feet</td>
                    </tr>
                    <tr className="border-b border-neutral-200">
                      <td className="p-4 text-neutral-700">Aerial Fireworks</td>
                      <td className="p-4 text-neutral-700">50 feet from buildings</td>
                      <td className="p-4 text-neutral-700">150 feet</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Lighting Procedures */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🔥</span>
                Safe Lighting Procedures
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-neutral-900">Step-by-Step Process</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3">
                      <span className="bg-accent-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">1</span>
                      <p className="text-neutral-700">Place firework on stable, flat surface away from spectators</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-accent-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">2</span>
                      <p className="text-neutral-700">Light fuse with long lighter or extended lighter wand</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-accent-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">3</span>
                      <p className="text-neutral-700">Move to safe distance immediately after lighting</p>
                    </div>
                    <div className="flex items-start space-x-3">
                      <span className="bg-accent-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-semibold">4</span>
                      <p className="text-neutral-700">Never lean over firework or look down the tube</p>
                    </div>
                  </div>
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">What NOT to Do</h3>
                  <ul className="space-y-2 text-yellow-700">
                    <li>• ❌ Never hold fireworks in your hand while lighting</li>
                    <li>• ❌ Don't use matches or short lighters</li>
                    <li>• ❌ Never light multiple fireworks at once</li>
                    <li>• ❌ Don't try to relight duds or malfunctions</li>
                    <li>• ❌ Never modify or disassemble fireworks</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Weather Considerations */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🌤️</span>
                Weather Considerations
              </h2>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-red-50 rounded-lg p-4 text-center">
                  <div className="text-3xl mb-2">☀️</div>
                  <h3 className="font-semibold text-red-800 mb-2">Hot & Dry</h3>
                  <p className="text-red-700 text-sm">HIGH RISK - Avoid use during drought conditions or high fire danger</p>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-3xl mb-2">🌧️</div>
                  <h3 className="font-semibold text-blue-800 mb-2">Wet Weather</h3>
                  <p className="text-blue-700 text-sm">UNSAFE - Never use fireworks in rain or wet conditions</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <div className="text-3xl mb-2">🌙</div>
                  <h3 className="font-semibold text-green-800 mb-2">Calm Evening</h3>
                  <p className="text-green-700 text-sm">IDEAL - Cool, calm weather with no wind is best</p>
                </div>
              </div>
            </section>

            {/* Emergency Procedures */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">🚑</span>
                Emergency Procedures
              </h2>
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <h3 className="font-semibold text-red-800 mb-2">In Case of Fire</h3>
                    <ol className="text-red-700 text-sm space-y-1 list-decimal list-inside">
                      <li>Call emergency services immediately (Fire: 101)</li>
                      <li>Use water or fire extinguisher if safe to do so</li>
                      <li>Evacuate the area if fire spreads</li>
                      <li>Keep spectators away from danger</li>
                    </ol>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <h3 className="font-semibold text-orange-800 mb-2">For Injuries</h3>
                    <ol className="text-orange-700 text-sm space-y-1 list-decimal list-inside">
                      <li>Call emergency medical services (108/102)</li>
                      <li>Provide first aid if trained to do so</li>
                      <li>Cool burns with clean, cold water</li>
                      <li>Do not remove embedded objects</li>
                    </ol>
                  </div>
                </div>
                
                <div className="bg-neutral-100 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Emergency Contact Numbers</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="text-center">
                      <p className="font-semibold text-neutral-900">Fire Emergency</p>
                      <p className="text-2xl font-bold text-red-600">101</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-neutral-900">Medical Emergency</p>
                      <p className="text-2xl font-bold text-blue-600">108/102</p>
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-neutral-900">Police</p>
                      <p className="text-2xl font-bold text-green-600">100</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Storage & Transportation */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">📦</span>
                Storage & Transportation
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Safe Storage</h3>
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Store in cool, dry place away from heat sources</li>
                      <li>• Keep in original packaging until use</li>
                      <li>• Away from children and pets</li>
                      <li>• Separate from flammable materials</li>
                      <li>• Check expiration dates regularly</li>
                    </ul>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-neutral-900 mb-3">Transportation</h3>
                  <div className="bg-neutral-50 rounded-lg p-4">
                    <ul className="space-y-2 text-neutral-700">
                      <li>• Transport in original packaging</li>
                      <li>• Keep in well-ventilated vehicle</li>
                      <li>• Avoid smoking or open flames</li>
                      <li>• Don't leave in hot cars</li>
                      <li>• Handle gently to avoid damage</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* Legal Compliance */}
            <section>
              <h2 className="text-2xl font-bold text-neutral-900 mb-6 flex items-center">
                <span className="mr-3">⚖️</span>
                Legal Compliance
              </h2>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-800 mb-3">Know Your Local Laws</h3>
                <div className="text-blue-700 space-y-3">
                  <p>Fireworks regulations vary by location. Before using any fireworks:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Check local municipal laws and restrictions</li>
                    <li>Verify permitted usage dates and times</li>
                    <li>Obtain required permits if necessary</li>
                    <li>Respect noise ordinances and neighbor considerations</li>
                    <li>Follow environmental protection guidelines</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Additional Resources */}
            <section className="bg-accent-50 rounded-xl p-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-4">Additional Safety Resources</h2>
              <p className="text-neutral-700 mb-4">
                For more safety information or if you have questions about safe fireworks use:
              </p>
              {loading ? (
                <div className="flex items-center justify-center h-20">
                  <div className="text-neutral-600">Loading contact information...</div>
                </div>
              ) : companyData ? (
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Safety Hotline</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="mr-2">📞</span>
                        <span className="text-neutral-700">{companyData.phone}</span>
                      </div>
                      <div className="flex items-center">
                        <span className="mr-2">✉️</span>
                        <span className="text-neutral-700">{companyData.email}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-neutral-900 mb-2">Online Resources</h3>
                    <div className="space-y-1 text-neutral-700 text-sm">
                      <p>• Safety videos and tutorials</p>
                      <p>• Product-specific instructions</p>
                      <p>• Local regulation database</p>
                      <p>• Emergency procedure guides</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-neutral-600">Contact information unavailable. Please try again later.</div>
              )}
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Safety;